import {useCallback, useEffect, useRef, useState} from 'react';
import * as Yup from 'yup';
import {Formik} from 'formik';
import {isEmpty, lowerCase} from 'lodash';
import {connect} from 'react-redux';
import {NavLink} from 'react-router-dom';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import Logo from '../../../assets/svgs/logo.svg';
import es from 'react-phone-input-2/lang/es.json';
import {CircularProgress} from '@material-ui/core';
import {Actions} from '../../../internals/app/Actions';
import UserRepository from '../../../internals/repository/UserRepository';
import AuthRepository from '../../../internals/repository/AuthRepository';
import Recaptcha, {
  RecaptchaHandle,
  recaptchaEnabled,
} from '../../../components/Recaptcha';
import {
  AuthLayout,
  AuthEyebrow,
  AuthHeading,
  AuthFooterNote,
  AirField,
  AirPassword,
  PrimaryButton,
  GoogleButton,
  Label,
  FieldError,
  BRAND_DARK,
  BODY,
  BORDER,
  INK,
} from '../components/AuthUI';

/**
 * Sign up — Netlise Air treatment. Layout and form primitives come from
 * ../components/AuthUI, shared with the sign-in screen; see the notes there
 * for why these screens are styled literally rather than from the dashboard's
 * tokens.
 *
 * The existing wiring is unchanged: Formik + Yup, the redux `signUp` action,
 * Cognito federated (Google) sign-in, and IP-based country detection for the
 * phone field.
 */
const SignUpView = (props: any) => {
  const {signUp, loadingAction} = props;
  const {action, loading} = loadingAction;
  const [country, setCountry] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const getCoutry = async () => {
    await UserRepository.fetchCoutry((data: any) => {
      setCountry(data);
    });
  };

  useEffect(() => {
    getCoutry();
  }, []);

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .min(2, 'Must be 2 characters or more')
      .required('First name is required'),
    lastName: Yup.string()
      .min(2, 'Must be 2 characters or more')
      .required('Last name is required'),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),
    phoneNumber: Yup.string()
      .min(10, 'Enter valid phone number')
      .required('Must enter a phone number'),
    password: Yup.string()
      .min(8, 'Must be 8 characters or more')
      .required('Password is required'),
    // Was optional before, so an empty confirmation passed validation and the
    // mismatch only surfaced at the backend.
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Please confirm your password'),
    terms: Yup.boolean()
      .required('The terms and conditions must be accepted.')
      .oneOf([true], 'The terms and conditions must be accepted.'),
  });

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    confirmPassword: '',
    terms: false,
  };

  // null until the checkbox is solved. Verified for real by the pool's
  // pre-sign-up trigger; the button gate is only so the user finds out
  // before submitting rather than after a rejected sign-up.
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<RecaptchaHandle>(null);
  const onRecaptchaChange = useCallback(
    (token: string | null) => setRecaptchaToken(token),
    [],
  );

  const onClickSubmit = (values: any) => {
    signUp(values, values.password, recaptchaToken);
    // A v2 token is single-use; clear the tick so a failed attempt cannot be
    // retried with one Google will now reject.
    recaptchaRef.current?.reset();
  };

  const federatedLogin = async () => {
    await AuthRepository.federatedSignIn();
  };

  const loadingSignup = loading && action.type === 'SIGNUP';

  return (
    <AuthLayout
      logo={Logo}
      image="/images/shopping-mall.jpg"
      imageAlt="A Netlise merchant storefront"
      imagePosition="right center"
    >
      <div>
        <AuthEyebrow tag="NEW" label="Create your store" />
        <div style={{marginTop: 18}}>
          <AuthHeading>Sign up with Netlise</AuthHeading>
        </div>
      </div>

      <Formik
        onSubmit={(values) => onClickSubmit(values)}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({values, errors, touched, handleSubmit, handleChange, setFieldValue}) => {
          const phoneInvalid = !isEmpty(errors.phoneNumber) && !!touched.phoneNumber;
          return (
            <form
              onSubmit={handleSubmit}
              style={{display: 'flex', flexDirection: 'column', gap: 20}}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,180px),1fr))',
                  gap: 16,
                }}
              >
                <AirField
                  id="firstName"
                  label="First name"
                  placeholder="First name"
                  value={values.firstName}
                  error={errors.firstName}
                  touched={touched.firstName}
                  onChange={handleChange('firstName')}
                />
                <AirField
                  id="lastName"
                  label="Last name"
                  placeholder="Last name"
                  value={values.lastName}
                  error={errors.lastName}
                  touched={touched.lastName}
                  onChange={handleChange('lastName')}
                />
                <AirField
                  id="email"
                  type="email"
                  label="Your email"
                  placeholder="name@company.com"
                  value={values.email}
                  error={errors.email}
                  touched={touched.email}
                  onChange={handleChange('email')}
                />

                {/* react-phone-input-2 gives the real flag picker, restyled to
                    the 48px Air box. Country defaults to the visitor's detected
                    one, falling back to LK. */}
                <label style={{display: 'block'}}>
                  <Label>Phone Number</Label>
                  <PhoneInput
                    localization={es}
                    value={values.phoneNumber}
                    onChange={handleChange('phoneNumber')}
                    country={lowerCase(country?.country_code) || 'lk'}
                    containerStyle={{width: '100%'}}
                    inputStyle={{
                      height: 48,
                      width: '100%',
                      borderRadius: 14,
                      border: `1px solid ${phoneInvalid ? '#EF4444' : BORDER}`,
                      background: '#F9FAFB',
                      fontSize: 15,
                      color: INK,
                    }}
                    buttonStyle={{
                      border: `1px solid ${phoneInvalid ? '#EF4444' : BORDER}`,
                      borderRight: 'none',
                      borderTopLeftRadius: 14,
                      borderBottomLeftRadius: 14,
                      background: '#F9FAFB',
                    }}
                  />
                  <FieldError>
                    {phoneInvalid ? (errors.phoneNumber as string) : undefined}
                  </FieldError>
                </label>
              </div>

              <AirPassword
                id="password"
                label="Password"
                value={values.password}
                error={errors.password}
                touched={touched.password}
                onChange={handleChange('password')}
                shown={showPassword}
                onToggle={() => setShowPassword((v) => !v)}
              />
              <AirPassword
                id="confirmPassword"
                label="Confirm password"
                value={values.confirmPassword}
                error={errors.confirmPassword}
                touched={touched.confirmPassword}
                onChange={handleChange('confirmPassword')}
                shown={showConfirm}
                onToggle={() => setShowConfirm((v) => !v)}
              />

              <div>
                <label htmlFor="terms" style={{display: 'flex', alignItems: 'center', gap: 10}}>
                  <input
                    id="terms"
                    type="checkbox"
                    checked={values.terms}
                    onChange={(e) => setFieldValue('terms', e.target.checked)}
                    style={{width: 18, height: 18, accentColor: '#6366F1', flexShrink: 0}}
                  />
                  <span style={{fontSize: 14, color: BODY}}>
                    I accept the{' '}
                    <NavLink to="/signup" style={{color: BRAND_DARK, fontWeight: 500}}>
                      Terms and Conditions
                    </NavLink>
                  </span>
                </label>
                <FieldError>
                  {!isEmpty(errors.terms) && touched.terms
                    ? (errors.terms as string)
                    : undefined}
                </FieldError>
              </div>

              <Recaptcha ref={recaptchaRef} onChange={onRecaptchaChange} />

              <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                <PrimaryButton
                  busy={loadingSignup || (recaptchaEnabled() && !recaptchaToken)}
                >
                  {loadingSignup ? (
                    <CircularProgress size={18} style={{color: '#fff'}} />
                  ) : (
                    'Create an account'
                  )}
                </PrimaryButton>
                <GoogleButton busy={loadingSignup} onClick={federatedLogin}>
                  Sign up with Google
                </GoogleButton>
              </div>

              <AuthFooterNote>
                Already have an account?{' '}
                <NavLink to="/signin" style={{color: BRAND_DARK, fontWeight: 600}}>
                  Login here
                </NavLink>
              </AuthFooterNote>
            </form>
          );
        }}
      </Formik>
    </AuthLayout>
  );
};

export default connect(
  (state: any) => ({
    loadingAction: state.common.get('loadingAction'),
    selectedShop: state.dashboard.get('selectedShop'),
  }),
  {
    signUp: Actions.login.signUp,
  }
)(SignUpView);
